import { Stage } from "../../shared/CaseConfiguration";
import { percentage } from "../../shared/percentage";
import {
  DecisionTreeOfParty,
  IssueOutcome,
  IssueResult,
  JudgmentFor,
  PaymentPlan,
  TrialOutcome,
} from "../../shared/tree";
import {
  badge,
  card,
  font,
  GraphColors,
  graphStart,
  sectionLabel,
  signedTone,
  table,
  td,
  text,
  tr,
} from "./graphDot";

/** What the drawing of the decision trees takes from the app. */
export interface DecisionTreeLook {
  colors: GraphColors;
  money: (value: number) => string;
  stageName: (stage: Stage) => string;
  partyName: (partyId: string) => string;
  isClient: (partyId: string) => boolean;
  /** What a party has to pay with; undefined for a party the case does not know. */
  assetsOf: (partyId: string) => number | "unlimited" | undefined;
}

/**
 * Dev tool: the decision trees of the parties of a case as one graph in the
 * DOT language, a tree per party. Plain dev strings throughout, no i18n; the
 * names of stages are the app's.
 *
 * A tree reads from left to right in the things there are to read, each a
 * card: the party with its way to the judgment, the outcomes the trial can
 * have, and per outcome the plans by which it gets paid. What only passes
 * through (a stage, a single payment) is a row of a card, not a node: a tree
 * of nodes for each of them is several times as wide and no longer readable
 * at the width of a page.
 */
export function decisionTreesDot(
  trees: DecisionTreeOfParty[],
  look: DecisionTreeLook,
): string {
  const lines = graphStart(look.colors);
  // Graphviz lays a graph out from top to bottom and turns it on its side for
  // one that reads from left to right, which puts what was drawn first at the
  // bottom: the trees are given last to first, for the first party to be on top.
  trees
    .map((tree, index) => treeOf(tree, `p${index}`, look))
    .reverse()
    .forEach((tree) => lines.push(...tree));
  lines.push("}");
  return lines.join("\n");
}

const RESULT_BADGE: { [result in IssueResult]: string } = {
  won: "Won",
  lost: "Lost",
  either: "Won or lost",
};

function treeOf(
  tree: DecisionTreeOfParty,
  id: string,
  look: DecisionTreeLook,
): string[] {
  const { colors, money, partyName } = look;
  const signed = (value: number) => (value > 0 ? "+" : "") + money(value);
  // a cost is what the party is down by; no cost is not "minus nothing"
  const cost = (value: number) => money(value === 0 ? 0 : -value);
  const quiet = (content: string, size = 9.5) =>
    font(content, { size, color: colors.quiet });
  const figure = (value: number, size = 9.5) =>
    font(money(value), {
      size,
      color: signedTone(colors, value).ink,
      bold: true,
    });

  // a net result: the figure a branch of the tree ends in
  const netResult = (value: number) => {
    const tone = signedTone(colors, value);
    return table(
      [
        tr([
          td(font("Net result", { size: 8.5, color: tone.ink }), { pad: 4 }),
          td(font(money(value), { size: 12, color: tone.ink, bold: true }), {
            align: "RIGHT",
            pad: 4,
          }),
        ]),
      ],
      { border: 1, color: tone.ink, bg: tone.wash, pad: 2 },
    );
  };

  const out: string[] = [];

  // ---- the party, and its way to the judgment
  const paid = tree.stages.filter(({ cost }) => cost !== 0);
  const free = tree.stages.length - paid.length;
  out.push(
    `  ${id} [label=<${card(colors, [
      tr([td(sectionLabel(colors, "Party"), { colspan: 2, pad: 7 })]),
      tr([
        td(font(text(partyName(tree.partyId)), { size: 12.5, bold: true })),
        td(
          look.isClient(tree.partyId)
            ? badge("Client", { ink: colors.primary, wash: colors.soft })
            : " ",
          { align: "RIGHT" },
        ),
      ]),
      tr([
        td(quiet("Expected value")),
        td(figure(tree.expectedValue, 13), { align: "RIGHT" }),
      ]),
      tr([
        td(sectionLabel(colors, "Costs up to the judgment"), {
          colspan: 2,
          pad: 7,
          rule: colors.rule,
        }),
      ]),
      tr([
        td(quiet("Incurred so far")),
        td(font(cost(tree.incurred), { size: 9.5 }), { align: "RIGHT" }),
      ]),
      ...paid.map((stage) =>
        tr([
          td(quiet(text(look.stageName(stage.stage)))),
          td(font(cost(stage.cost), { size: 9.5 }), { align: "RIGHT" }),
        ]),
      ),
      ...(free > 0
        ? [
            tr([
              td(
                quiet(
                  free === 1
                    ? "1 stage without further costs"
                    : `${free} stages without further costs`,
                  8.5,
                ),
                { colspan: 2 },
              ),
            ]),
          ]
        : []),
      tr([
        td(font("Before the judgment", { size: 9.5, bold: true }), {
          rule: colors.rule,
        }),
        td(font(money(tree.beforeJudgment), { size: 10.5, bold: true }), {
          align: "RIGHT",
          rule: colors.rule,
        }),
      ]),
    ])}>];`,
  );
  // a case with nothing left to try (every claim settled) ends at the party
  if (tree.outcomes.length > 0)
    out.push(
      `  ${id}_trial [shape=circle, style=filled, fillcolor="${colors.primary}", color="${colors.primary}", fixedsize=true, width=0.62, label=<${font("TRIAL", { size: 8, color: colors.surface, bold: true })}>];`,
      `  ${id} -> ${id}_trial [arrowhead=none, color="${colors.primary}"];`,
    );

  // ---- the outcomes of the trial
  const issueRows = (issues: IssueOutcome[]) =>
    issues.map(({ title, result }) =>
      tr([
        td(font(text(title), { size: 9.5 })),
        td(
          badge(
            RESULT_BADGE[result],
            result === "won"
              ? colors.favourable
              : result === "lost"
                ? colors.adverse
                : colors.neutral,
          ),
          { align: "RIGHT" },
        ),
      ]),
    );
  const head = (outcome: TrialOutcome) => {
    const own = outcome.issues.filter(({ ofParty }) => ofParty);
    const others = outcome.issues.filter(({ ofParty }) => !ofParty);
    return [
      tr([
        td(sectionLabel(colors, "Outcome of the trial"), {
          colspan: 2,
          pad: 7,
        }),
      ]),
      ...issueRows(own),
      ...(others.length > 0
        ? [
            tr([td(quiet("Between other parties", 8.5), { colspan: 2 })]),
            ...issueRows(others),
          ]
        : []),
      ...(outcome.scenarios > 1
        ? [
            tr([
              td(
                quiet(
                  `${outcome.scenarios} scenarios end in these payments`,
                  8.5,
                ),
                { colspan: 2 },
              ),
            ]),
          ]
        : []),
    ];
  };

  const assets = (partyId: string) => {
    const has = look.assetsOf(partyId);
    return has === undefined || has === "unlimited"
      ? ""
      : `, has ${money(has)}`;
  };
  const awarded = ({ owed }: JudgmentFor) =>
    owed.length === 0
      ? [tr([td(quiet("Nothing is awarded to anyone"), { colspan: 2 })])]
      : owed.map(({ recipientId, amount, payers }) =>
          tr([
            td(
              `${font(`${money(amount)} to ${text(partyName(recipientId))}`, { size: 9.5, bold: true })}<BR ALIGN="LEFT"/>${quiet(
                `payable by ${payers
                  .map(
                    ({ partyId, share }) =>
                      `${text(partyName(partyId))} (${percentage.format(share)}${assets(partyId)})`,
                  )
                  .join(", ")}`,
                8.5,
              )}`,
              { colspan: 2 },
            ),
          ]),
        );

  const planCard = (plan: PaymentPlan, position: string, isWorst: boolean) => {
    const tone = signedTone(colors, plan.netResult);
    return card(
      colors,
      [
        tr([
          td(
            `${sectionLabel(colors, position)}${isWorst ? `  ${font(" worst case ", { size: 8, color: colors.warning.ink, bold: true })}` : ""}`,
            { pad: 7 },
          ),
          td(
            plan.unpaid.length === 0
              ? badge("Paid in full", colors.favourable)
              : badge("Not paid in full", colors.warning),
            { align: "RIGHT" },
          ),
        ]),
        ...plan.payments.map(({ payerId, recipientId, amount, share }) => {
          // from the party's side: what it pays, what it gets, what passes it by
          const forParty =
            payerId === tree.partyId
              ? -amount
              : recipientId === tree.partyId
                ? amount
                : 0;
          return tr([
            td(
              font(
                `${text(partyName(payerId))} paid ${percentage.format(share)} to ${text(partyName(recipientId))}`,
                { size: 9.5 },
              ),
            ),
            td(
              forParty === 0
                ? quiet(money(amount))
                : font(signed(forParty), {
                    size: 9.5,
                    color: signedTone(colors, forParty).ink,
                    bold: true,
                  }),
              { align: "RIGHT" },
            ),
          ]);
        }),
        ...plan.unpaid.map(({ recipientId, amount }) =>
          tr([
            td(
              font(
                `${money(amount)} for ${text(partyName(recipientId))} unpaid`,
                { size: 8.5, color: colors.warning.ink },
              ),
              { colspan: 2 },
            ),
          ]),
        ),
        tr([
          td(netResult(plan.netResult), {
            colspan: 2,
            pad: 7,
            rule: colors.rule,
          }),
        ]),
      ],
      { outline: tone.ink },
    );
  };

  tree.outcomes.forEach((outcome, index) => {
    const outcomeId = `${id}_o${index}`;
    out.push(
      `  ${id}_trial -> ${outcomeId} [label=<${font(percentage.format(outcome.probability), { size: 12, color: colors.ink, bold: true })}>];`,
    );

    if (outcome.judgment === "against") {
      out.push(
        `  ${outcomeId} [label=<${card(
          colors,
          [
            ...head(outcome),
            tr([
              td(quiet("Pays, at worst"), { rule: colors.rule }),
              td(figure(-outcome.pays), { align: "RIGHT", rule: colors.rule }),
            ]),
            tr([td(netResult(outcome.netResult), { colspan: 2, pad: 7 })]),
          ],
          { outline: colors.adverse.ink },
        )}>];`,
      );
      return;
    }

    // with no way to be paid there is nothing to enforce: the branch ends here
    const ends = outcome.plans.length === 0;
    out.push(
      `  ${outcomeId} [label=<${card(colors, [
        ...head(outcome),
        tr([
          td(sectionLabel(colors, "Awarded"), {
            colspan: 2,
            pad: 7,
            rule: colors.rule,
          }),
        ]),
        ...awarded(outcome),
        ends
          ? tr([
              td(netResult(outcome.netResultUnenforced), {
                colspan: 2,
                pad: 7,
                rule: colors.rule,
              }),
            ])
          : tr([
              td(quiet("Without enforcing", 9), { rule: colors.rule }),
              td(figure(outcome.netResultUnenforced), {
                align: "RIGHT",
                rule: colors.rule,
              }),
            ]),
      ])}>];`,
    );

    const results = outcome.plans.map(({ netResult }) => netResult);
    const worst = Math.min(...results);
    const differ = results.some((result) => result !== worst);
    outcome.plans.forEach((plan, planIndex) => {
      const planId = `${outcomeId}_p${planIndex}`;
      const position =
        outcome.plans.length > 1
          ? `Payment plan ${planIndex + 1} of ${outcome.plans.length}`
          : "Payment plan";
      out.push(
        `  ${planId} [label=<${planCard(plan, position, differ && plan.netResult === worst)}>];`,
        `  ${outcomeId} -> ${planId} [label=<Enforce${
          outcome.enforcementCost !== 0
            ? `<BR/>${font(cost(outcome.enforcementCost), { color: colors.adverse.ink, bold: true })}`
            : ""
        }>];`,
      );
    });
  });
  return out;
}
